"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { Menu, Search, X, Heart, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerContent, DrawerTrigger, DrawerClose, DrawerTitle } from "@/components/ui/drawer";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative inline-flex h-10 items-center px-1 text-[13px] uppercase tracking-[0.2em] font-heading font-semibold text-foreground hover:text-accent transition-all duration-300 bg-transparent hover:bg-transparent focus:bg-transparent group focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 outline-none rounded-md">
                <span>{item.label}</span>
                <span className="pointer-events-none absolute bottom-1 left-0 right-0 h-0.5 origin-left scale-x-0 bg-accent transition-transform duration-500 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link to="/MyAccountPage" className="relative inline-flex h-10 items-center px-1 text-[13px] uppercase tracking-[0.2em] font-heading font-semibold text-foreground hover:text-accent transition-all duration-300 bg-transparent hover:bg-transparent focus:bg-transparent group focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 outline-none rounded-md">
              <span>My Account</span>
              <span className="pointer-events-none absolute bottom-1 left-0 right-0 h-0.5 origin-left scale-x-0 bg-accent transition-transform duration-500 ease-out group-hover:scale-x-100" />
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-8 w-3/4 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-6">
      {menuItems.map((item, i) => <DrawerClose asChild key={item.id}>
          <Link data-index={i} to={item.href || "#"} className="block py-4 font-heading font-semibold text-2xl text-foreground hover:text-accent transition-colors border-b border-border/40 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 outline-none rounded-md">
            {item.label}
          </Link>
        </DrawerClose>)}
      <DrawerClose asChild>
        <Link to="/MyAccountPage" className="block py-4 font-heading font-semibold text-2xl text-foreground hover:text-accent transition-colors border-b border-border/40 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 outline-none rounded-md">
          My Account
        </Link>
      </DrawerClose>
    </nav>;
}
export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <Link to="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-6 focus:py-3 focus:bg-accent focus:text-accent-foreground focus:font-bold focus:ring-4 focus:ring-primary focus:rounded-md">
        Skip to main content
      </Link>
      <div className="w-full bg-primary text-primary-foreground py-2.5 px-6 text-center text-[10px] md:text-xs font-mono uppercase tracking-[0.2em] border-b border-accent/30">
        Free Shipping on Orders Over $100 | Easy Returns | Secure Checkout
      </div>
      <section data-section-id="405" id="main-navigation" className={`sticky top-0 z-50 w-full bg-background text-foreground border-b transition-all duration-500 ${isScrolled ? "bg-background/95 backdrop-blur-md border-border shadow-sm" : "bg-background border-border"}`}>
        <div className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")"
      }} aria-hidden="true" />

        <div className="relative mx-auto flex h-20 md:h-24 max-w-7xl items-center justify-between px-6 md:px-10 transition-all duration-300">
          <div className="flex items-center">
            <Link to="/" aria-label="Drip District — Home" className="flex items-center group focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 outline-none rounded-md">
              <WvcLogo className="h-14 md:h-16 lg:h-20 w-auto transition-transform duration-500 group-hover:scale-105" />
            </Link>
          </div>

          <div className="hidden lg:flex flex-1 items-center justify-center">
            <MenuProvider menu_id="22">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center gap-1 md:gap-2">
            <div className="hidden md:flex items-center">
              {searchOpen ? <div className="flex items-center gap-3 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="relative">
                    <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search collections..." className="w-64 [&_input]:bg-transparent [&_input]:border-0 [&_input]:border-b-2 [&_input]:border-foreground [&_input]:rounded-md [&_input]:px-1 [&_input]:focus-visible:ring-2 [&_input]:focus-visible:ring-accent [&_input]:focus-visible:border-accent [&_input]:font-mono [&_input]:text-xs [&_input]:tracking-widest [&_input]:uppercase transition-all" />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => {
                setSearchOpen(false);
                setSearchValue("");
              }} aria-label="Close search" className="h-10 w-10 text-foreground hover:bg-secondary hover:text-accent rounded-md transition-all duration-300 focus-visible:ring-2 focus-visible:ring-accent">
                    <X className="size-5" />
                  </Button>
                </div> : <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} aria-label="Open search" className="h-10 w-10 text-foreground hover:bg-secondary hover:text-accent transition-all duration-300 rounded-md focus-visible:ring-2 focus-visible:ring-accent">
                  <Search className="size-5" />
                </Button>}
            </div>

            <div className="hidden xl:flex items-center px-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-muted-foreground border-l border-r border-border h-8">
              <span className="text-accent">LSH</span>
              <span className="ml-2">ATELIER</span>
            </div>

            <Button variant="ghost" size="icon" asChild className="hidden md:inline-flex h-10 w-10 text-foreground hover:bg-secondary hover:text-accent transition-all duration-300 rounded-md focus-visible:ring-2 focus-visible:ring-accent">
              <Link to="#" aria-label="Wishlist" className="focus-visible:ring-0 outline-none">
                <Heart className="size-5" />
              </Link>
            </Button>

            <Button variant="ghost" size="icon" asChild className="hidden md:inline-flex h-10 w-10 text-foreground hover:bg-secondary hover:text-accent transition-all duration-300 rounded-md focus-visible:ring-2 focus-visible:ring-accent">
              <Link to="/MyAccountPage" aria-label="My Account" className="focus-visible:ring-0 outline-none">
                <User className="size-5" />
              </Link>
            </Button>

            <CartIcon variant="ghost" size="icon" iconSize="size-5" className="h-10 w-10 inline-flex items-center justify-center text-foreground hover:bg-secondary hover:text-accent transition-all duration-300 rounded-md focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 outline-none" />

            <SafeHydrate><Drawer open={mobileOpen} onOpenChange={setMobileOpen} direction="right">
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden h-10 w-10 text-foreground hover:bg-secondary hover:text-accent rounded-md transition-all duration-300 focus-visible:ring-2 focus-visible:ring-accent" aria-label="Open menu">
                  <Menu className="size-6" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="bg-background text-foreground border-l border-border h-full w-[88%] sm:w-[420px]">
                <DrawerTitle className="sr-only">Navigation Menu</DrawerTitle>

                <div className="flex items-center justify-between px-8 pt-8 pb-10 border-b border-border">
                  <Link to="/" aria-label="Drip District — Home" onClick={() => setMobileOpen(false)} className="focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 outline-none rounded-md">
                    <WvcLogo className="h-10 w-auto" />
                  </Link>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" asChild className="h-10 w-10 text-foreground hover:bg-secondary hover:text-accent rounded-md transition-all duration-300 focus-visible:ring-2 focus-visible:ring-accent">
                      <Link to="#" aria-label="Wishlist" className="focus-visible:ring-0 outline-none">
                        <Heart className="size-6" />
                      </Link>
                    </Button>
                    <Button variant="ghost" size="icon" asChild className="h-10 w-10 text-foreground hover:bg-secondary hover:text-accent rounded-md transition-all duration-300 focus-visible:ring-2 focus-visible:ring-accent">
                      <Link to="/MyAccountPage" aria-label="My Account" className="focus-visible:ring-0 outline-none">
                        <User className="size-6" />
                      </Link>
                    </Button>
                    <DrawerClose asChild>
                      <Button variant="ghost" size="icon" aria-label="Close menu" className="h-10 w-10 text-foreground hover:bg-secondary hover:text-accent rounded-md transition-all duration-300 focus-visible:ring-2 focus-visible:ring-accent">
                        <X className="size-6" />
                      </Button>
                    </DrawerClose>
                  </div>
                </div>

                <div className="py-10 overflow-y-auto">
                  <div className="px-8 mb-10">
                    <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search collections..." className="w-full [&_input]:bg-transparent [&_input]:border-0 [&_input]:border-b-2 [&_input]:border-foreground [&_input]:rounded-md [&_input]:px-1 [&_input]:focus-visible:ring-2 [&_input]:focus-visible:ring-accent [&_input]:focus-visible:border-accent [&_input]:font-mono [&_input]:text-xs [&_input]:tracking-widest [&_input]:uppercase" />
                  </div>

                  <MenuProvider menu_id="22">
                    <MobileMenuConsumer />
                  </MenuProvider>

                  <div className="px-8 mt-12 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground leading-relaxed">
                    <p className="text-foreground font-semibold">DRIP DISTRICT — DIGITAL ATELIER</p>
                    <p className="mt-2">GLOBAL SHIPPING · 24/7 SUPPORT</p>
                  </div>
                </div>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </section>
    </>;
}